import { plural, t } from "@lingui/macro";
import {
  EApiDatafanPipelineStatus,
  EApiDatafanProgressType,
  EApiDatafanStreamReportStatus,
  EApiDatafanStreamStatus,
  IApiDatafanPipeline,
} from "@smp/api";
import { parse } from "date-fns";
import differenceInSeconds from "date-fns/differenceInSeconds";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import subMinutes from "date-fns/subMinutes";
import { useCallback } from "react";

import { useQueryDatafanProject } from "@/queries/datafan";
import { aggregateDatafanReportStatusProgress } from "@/utils";

export interface IAggregatedPipelineStatus {
  description?: string;
  title: string;
  to?: string;
}

const getWorkStatus = (pipeline: IApiDatafanPipeline) => {
  const now = new Date();
  const seconds = (() => {
    if (!pipeline.data_update_at) {
      return 1;
    }

    const dateBackend = subMinutes(
      parse(pipeline.backend_datetime, "yyyy-MM-dd HH:mm", now),
      now.getTimezoneOffset(),
    );
    const dateUpdateAt = parseISO(pipeline.data_update_at);
    const result = differenceInSeconds(dateBackend, dateUpdateAt);

    return Math.max(1, Number.isNaN(result) ? 1 : result);
  })();
  const d = Math.floor(seconds / 3600 / 24);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor(seconds / 60);
  const time = (d && t`${d}d.`) || (h && t`${h}h.`) || t`${m || 1}m.`;
  const nextUpdateAt = pipeline.streams.find(
    (stream) => stream.next_update_at,
  )?.next_update_at;

  return {
    description: nextUpdateAt
      ? t`Next update: ${format(
          subMinutes(
            parse(nextUpdateAt, "yyyy-MM-dd HH:mm", now),
            now.getTimezoneOffset(),
          ),
          "d MMMM, HH:mm",
        )}`
      : undefined,
    title: pipeline.data_update_at ? t`Updated ${time} ago` : t`Updated`,
  } as IAggregatedPipelineStatus;
};

export const useDatafanPipelineStatus = () => {
  const { data: project } = useQueryDatafanProject();

  return useCallback(
    (pipeline: IApiDatafanPipeline): IAggregatedPipelineStatus => {
      const accountName = pipeline.streams[0].config_source.entity_account_name;

      if (pipeline.status === EApiDatafanPipelineStatus.ARCHIVED) {
        return {
          description: t`In this status, data is no longer collected. But the old data is available.`,
          title: t`Archived`,
        };
      }

      for (const element of pipeline.streams) {
        if (element.status === EApiDatafanStreamStatus.CREDENTIAL_LOST) {
          if (project?.role === "owner") {
            return {
              description: t`Please update your data source connection.`,
              title: t`Source lost`,
              to: "/credentials/",
            };
          } else {
            return {
              description: t`The credentials ${
                accountName || ""
              } were removed from the project ${
                project?.name || ""
              }. Contact the owner of the credentials.`,
              title: t`Access denied`,
            };
          }
        } else if (
          [
            EApiDatafanStreamStatus.CREDENTIAL_NOT_PERMISSION,
            EApiDatafanStreamStatus.CREDENTIAL_NOT_ACCOUNT_PERMISSION,
          ].includes(element.status)
        ) {
          return {
            description: t`Your social account access level is insufficient for data collection. Please request appropriate access level (Admin level of editor level) from Admin.`,
            title: t`Access denied`,
            to: "/credentials/",
          };
        } else if (
          element.status === EApiDatafanStreamStatus.CREDENTIAL_LOST_SHARED
        ) {
          if (project?.role === "owner") {
            return {
              description:
                t`Go to the project settings and return the shared access to the credentials` +
                `${(accountName && ": " + accountName) + "."}`,
              title: t`Credentials revoked`,
            };
          } else {
            return {
              description:
                t`Credentials revoked. Please contact credential owner` +
                `${(accountName && ": " + accountName) + "."}`,
              title: t`Credentials revoked`,
            };
          }
        }
      }

      if (
        pipeline.report_status.some(
          (rs) =>
            rs.status !== EApiDatafanStreamReportStatus.SUCCESS &&
            rs.retry_count >= 5,
        )
      ) {
        return {
          description: t`Contact us for support`,
          title: t`Data collection error`,
        };
      }

      let repeatActive = false;
      for (const element of pipeline.report_status) {
        if (
          element.status === EApiDatafanStreamReportStatus.REPEAT ||
          element.status === EApiDatafanStreamReportStatus.FORCE_REPEAT
        ) {
          repeatActive = true;
          break;
        }
      }

      if (
        repeatActive ||
        (!pipeline.report_status.some(({ status }) => status !== null) &&
          pipeline.status === EApiDatafanPipelineStatus.DATA_COLLECTION)
      ) {
        return {
          description: t`Data collection will start within 2-3 minutes`,
          title: t`Awaiting launch`,
        };
      }

      const completedStreamsLength = pipeline.report_status.filter(
        ({ status }) => status === EApiDatafanStreamReportStatus.SUCCESS,
      ).length;

      if (completedStreamsLength < pipeline.streams.length) {
        const progress = aggregateDatafanReportStatusProgress(
          pipeline.report_status,
        );
        const progressStringValue = (() => {
          if (!progress.stepTotal) {
            return `${
              pipeline.streams.length > 1
                ? completedStreamsLength +
                  " " +
                  t`of` +
                  " " +
                  pipeline.streams.length +
                  " " +
                  t`completed`
                : ""
            }`;
          }

          if (
            progress.type === EApiDatafanProgressType.TIMER &&
            typeof progress.value === "number"
          ) {
            const mm = Math.floor(progress.value / 60);
            const ss = progress.value - mm * 60;

            if (progress.sign === "fast_run") {
              const mmStr = plural(mm, {
                few: "# minutes",
                many: "# minutes",
                one: "# minute",
                other: "# minutes",
              });
              const ssStr = plural(ss, {
                few: "# seconds",
                many: "# seconds",
                one: "# second",
                other: "# seconds",
              });
              const timerString = `${mm ? mmStr + " " : ""}${ssStr}`;

              return t`The first stage of data collection.` + progress.value
                ? " " + t`Remained` + ": " + timerString
                : "";
            }

            return `${progress.name ? progress.name + ": " : ""}${
              mm <= 9 ? "0" + mm : mm
            }:${ss}`;
          }

          if (progress.name !== "" && progress.value !== "") {
            progress.name += ": ";
          }

          return `${progress.name}${progress.value}`;
        })();

        const progressLabel = (() => {
          let str = t`Data collection`;

          if (progress.stepTotal > 1) {
            str += `. ${t`Step`}: ${progress.stepCurrent} ${t`of`} ${
              progress.stepTotal
            }`;
          }

          return str;
        })();

        return {
          description: progressStringValue,
          title: progressLabel,
        };
      }

      return getWorkStatus(pipeline);
    },
    [project?.name, project?.role],
  );
};
